import { useCallback, useState } from 'react';
import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import type { AxiosResponse, AxiosError } from 'axios';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import { useTranslation } from 'react-i18next';
import { environmentAtom } from '@data/fms/environment/states';
import type {
  Vehicle,
  VehicleAcceptableOrder,
  VehicleCategory,
} from '@data/fms/vehicle/types';
import { useAtomCallback } from 'jotai/utils';

type EnvironmentBody = {
  path: '/environment_id';
  value: string;
};

type AcceptableOrderBody = {
  path: '/acceptable_order';
  value: VehicleAcceptableOrder;
};

type CanStartBody = {
  path: '/can_start';
  value: boolean;
};

type DescriptionBody = {
  path: '/description';
  value: string;
};

type VehicleNameBody = {
  path: '/vehicle_name';
  value: string;
};

type InitialPointBody = {
  path: '/initial_point_id';
  value: number | null;
};

type CategoryBody = {
  path: '/category';
  value: VehicleCategory;
};

type AppFederationBody = {
  path: '/enable_in_vehicle_app_federation';
  value: boolean;
};

type Body =
  | AcceptableOrderBody
  | CanStartBody
  | DescriptionBody
  | VehicleNameBody
  | InitialPointBody
  | CategoryBody
  | AppFederationBody;
/**
 * 指定車両の情報更新API
 * - 走行環境変更
 * - 配車受付変更
 * - 発車受付変更
 * - 車両名変更
 * - 初期位置変更
 */
export const usePatchVehiclesAPI = (): {
  state: APIState;
  patchChangeEnvironment: (
    vehicleId: string,
    environmentId: string,
  ) => Promise<Vehicle | null>;
  patchAcceptableOrder: (
    vehicleId: string,
    acceptableOrder: VehicleAcceptableOrder,
  ) => Promise<Vehicle | null>;
  patchCanStart: (
    vehicleId: string,
    canStart: boolean,
  ) => Promise<Vehicle | null>;
  patchVehicleName: (
    vehicleId: string,
    vehicleName: string,
  ) => Promise<Vehicle | null>;
  patchInitialPointId: (
    vehicleId: string,
    initialPointId: number | null,
  ) => Promise<Vehicle | null>;
  patchAppFederation: (
    vehicleId: string,
    enableInVehicleAppFederation: boolean,
  ) => Promise<Vehicle | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const patchChangeEnvironmentAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      body: EnvironmentBody,
    ): Promise<Result<Vehicle, AxiosResponse>> => {
      try {
        const res = await fmsAPI.patch(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}`,
          [
            {
              ...body,
              op: 'replace',
            },
          ],
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const patchVehiclesAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      body: Body,
      params?: URLSearchParams,
    ): Promise<Result<Vehicle, AxiosResponse>> => {
      try {
        const paramsToStr = params ? `?${params.toString()}` : '';
        const res = await fmsAPI.patch(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}${paramsToStr}`,
          [
            {
              ...body,
              op: 'replace',
            },
          ],
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  /**
   * 走行環境変更
   * @param vehicleId
   * @param environmentId
   */
  const patchChangeEnvironment = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, environmentId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.patch_vehicles.driving_environment', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await patchChangeEnvironmentAPI(
          project.id,
          environment.environment_id,
          vehicleId,
          {
            path: '/environment_id',
            value: environmentId,
          },
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.patch_vehicles.driving_environment', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.patch_vehicles.driving_environment', {
            status: 'successfully',
          }),
          testIdAttrs: {
            wrapperTestId:
              'change_driving_environment_snackbar_success-status-div',
            closeButtonTestId:
              'change_driving_environment_snackbar_success-click-close_button',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [
        notifyError,
        notifySuccess,
        patchChangeEnvironmentAPI,
        t,
        getErrorMessage,
      ],
    ),
  );

  /**
   * 配車受付可否変更
   */
  const patchAcceptableOrder = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        vehicleId: string,
        acceptableOrder: VehicleAcceptableOrder,
      ) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.patch_vehicles.acceptable_order', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await patchVehiclesAPI(
          project.id,
          environment.environment_id,
          vehicleId,
          {
            path: '/acceptable_order',
            value: acceptableOrder,
          },
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.patch_vehicles.acceptable_order', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.patch_vehicles.acceptable_order', {
            status: 'successfully',
          }),
          testIdAttrs: {
            wrapperTestId: 'allocate_snackbar_success-status-div',
            closeButtonTestId: 'allocate_snackbar_success-click-close_button',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, patchVehiclesAPI, t, getErrorMessage],
    ),
  );

  /**
   * 発車指示受付可否変更
   */
  const patchCanStart = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, canStart: boolean) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.patch_vehicles.can_start', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await patchVehiclesAPI(
          project.id,
          environment.environment_id,
          vehicleId,
          {
            path: '/can_start',
            value: canStart,
          },
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.patch_vehicles.can_start', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.patch_vehicles.can_start', {
            status: 'successfully',
          }),
          testIdAttrs: {
            wrapperTestId: 'departure_snackbar_success-status-div',
            closeButtonTestId: 'departure_snackbar_success-click-close_button',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, patchVehiclesAPI, t, getErrorMessage],
    ),
  );

  /**
   * 車両名変更
   */
  const patchVehicleName = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, vehicleName: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.patch_vehicles.vehicle_name', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await patchVehiclesAPI(
          project.id,
          environment.environment_id,
          vehicleId,
          {
            path: '/vehicle_name',
            value: vehicleName,
          },
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.patch_vehicles.vehicle_name', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.patch_vehicles.vehicle_name', {
            status: 'successfully',
          }),
          testIdAttrs: {
            wrapperTestId: 'change_vehicle_name_snackbar_success-status-div',
            closeButtonTestId:
              'change_vehicle_name_snackbar_success-click-close_button',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, patchVehiclesAPI, t, getErrorMessage],
    ),
  );

  /**
   * 初期位置変更
   */
  const patchInitialPointId = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, initialPointId: number | null) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.patch_vehicles.initial_position', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const params = new URLSearchParams();
        params.append('area_map_version_id', environment.area_map_version_id);
        const res = await patchVehiclesAPI(
          project.id,
          environment.environment_id,
          vehicleId,
          {
            path: '/initial_point_id',
            value: initialPointId,
          },
          params,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.patch_vehicles.initial_position', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.patch_vehicles.initial_position', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, patchVehiclesAPI, t, getErrorMessage],
    ),
  );

  /**
   * AppFederation 変更
   */
  const patchAppFederation = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        vehicleId: string,
        enableInVehicleAppFederation: boolean,
      ) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.patch_vehicles.in_vehicle_app_federation', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await patchVehiclesAPI(
          project.id,
          environment.environment_id,
          vehicleId,
          {
            path: '/enable_in_vehicle_app_federation',
            value: enableInVehicleAppFederation,
          },
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.patch_vehicles.in_vehicle_app_federation', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.patch_vehicles.in_vehicle_app_federation', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, patchVehiclesAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    patchChangeEnvironment,
    patchAcceptableOrder,
    patchCanStart,
    patchVehicleName,
    patchInitialPointId,
    patchAppFederation,
  };
};
